import {useAtom} from "jotai";
import {useCallback, useEffect} from "react";
import {FlowProtectionSessionsAtom} from "../pages/project/atoms";
import {currentProtectionProjectId, readFlowProtection, writeFlowProtection} from "../utils/flowProtectionStorage";

const DEFAULT_TTL_SECONDS = 900; // 15 minutes, matches backend session length

/**
 * Tracks the per-project "protection unlock" session token used to view
 * protected user messages unmasked. Backed by an in-memory jotai atom
 * (for reactivity across mounted components) mirrored into localStorage
 * (see flowProtectionStorage), which ConversationRepository reads to attach the
 * X-Protection-Token header. The entry expires with the backend session.
 * projectId: registry project id, defaults to the open registry project.
 */
export const useFlowProtection = (projectId = currentProtectionProjectId()) => {
  const [sessions, setSessions] = useAtom(FlowProtectionSessionsAtom);

  // Hydrate this project's session from localStorage the first time it's used.
  useEffect(() => {
    if (!projectId || sessions[projectId]) return;
    const entry = readFlowProtection(projectId);
    if (entry) {
      setSessions(prev => ({...prev, [projectId]: entry}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // falls back to localStorage until hydrated, so the token is there from the first render (pages reload on a change)
  const session = projectId ? sessions[projectId] || readFlowProtection(projectId) : null;
  const isUnlocked = !!(session?.token && session?.expiresAt > Date.now());

  const unlock = useCallback((token, expiresInSeconds = DEFAULT_TTL_SECONDS) => {
    if (!projectId || !token) return;
    setSessions(prev => {
      const next = {...prev, [projectId]: {token, expiresAt: Date.now() + (expiresInSeconds || DEFAULT_TTL_SECONDS) * 1000}};
      writeFlowProtection(projectId, next[projectId]);
      return next;
    });
  }, [projectId, setSessions]);

  // Call after a request successfully used the token - the backend slides
  // the real session forward, this keeps the local expiry in sync.
  const extend = useCallback((expiresInSeconds = DEFAULT_TTL_SECONDS) => {
    if (!projectId) return;
    setSessions(prev => {
      if (!prev[projectId]?.token) return prev;
      const next = {...prev, [projectId]: {...prev[projectId], expiresAt: Date.now() + (expiresInSeconds || DEFAULT_TTL_SECONDS) * 1000}};
      writeFlowProtection(projectId, next[projectId]);
      return next;
    });
  }, [projectId, setSessions]);

  const lock = useCallback(() => {
    if (!projectId) return;
    setSessions(prev => {
      if (!(projectId in prev)) return prev;
      const next = {...prev};
      delete next[projectId];
      writeFlowProtection(projectId, null);
      return next;
    });
  }, [projectId, setSessions]);

  return {
    token: isUnlocked ? session.token : null,
    isUnlocked,
    expiresAt: session?.expiresAt || null,
    unlock,
    extend,
    lock,
  };
};
